import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { isReservedTopicSlug } from './topicHub';

/**
 * `/occasion/:id` -> `/gift-guides/:topic` (THE-587).
 *
 * The occasions slug is retired. The pages behind it rendered HARDCODED sample
 * product arrays compiled into the JS bundle, not live catalogue, so there was
 * nothing on them worth preserving — and keeping a second occasion URL family
 * alive would split link equity between two surfaces that answer the same
 * question. `/gift-guides/{topic}` is now the only one.
 *
 * REDIRECT, NOT 404, because the inbound links are real: the calendar's
 * Mother's Day / Father's Day / Teacher Appreciation cards route here, and
 * `/occasion/*` URLs have been live long enough to be bookmarked and linked.
 *
 * THE QUERY STRING IS CARRIED THROUGH, and that is not decoration. The hub
 * prototype's cohort lives entirely in `?giftGuideHub=1`; a redirect that
 * dropped it would silently eject a tester from the experiment at the exact
 * moment they followed a legacy link, and every guides-tab tap afterwards would
 * return the production page. This is the same defect the hop inventory in
 * `giftGuideHubFlag` exists to prevent, one layer lower.
 *
 * WHAT THE COHORT SEES vs EVERYONE ELSE. `/gift-guides/{topic}` is behind the
 * hub gate, so today a visitor without the cohort lands on the topic route and
 * is forwarded once more to the production guides index — a real page about the
 * same thing. When the release switch flips, the same URL resolves to the topic
 * page for everyone, with no further redirect and no code change. That is the
 * whole point of retiring onto the gated family rather than onto a guide.
 */

/**
 * Legacy occasion ids are snake_case (`mothers_day`); hub topic keys are
 * slug-shaped (`mothers-day`). Normalising here rather than at each call site
 * keeps the one translation in the one place that knows both vocabularies.
 */
export function occasionTopicSlug(occasionId: string): string {
  return (
    occasionId
      .trim()
      .toLowerCase()
      .replace(/_+/g, '-')
      // Trim the separators too, so an id that is NOTHING BUT separators
      // collapses to '' and hits the index fallback below. Without this,
      // `/occasion/___` normalised to the slug `-` and redirected to the
      // literal route `/gift-guides/-`, which searches the catalogue for a
      // hyphen. Caught by the fallback test, not by inspection.
      .replace(/^-+|-+$/g, '')
  );
}

/** `/gift-guides/<topic>` plus whatever query string arrived. */
export function occasionRedirectPath(occasionId: string, search = ''): string {
  const slug = occasionTopicSlug(occasionId);
  const normalizedSearch = search && !search.startsWith('?') ? `?${search}` : search;
  // An empty or all-separator id has no topic to land on; the index is the
  // honest destination rather than `/gift-guides/`.
  //
  // A RESERVED slug lands there too. `isReservedTopicSlug` stops a curated
  // occasion KEY from being `search`, but `/occasion/:id` takes whatever a
  // bookmark carries — so `/occasion/search` forwarded onto the search route
  // with no `?q=`, i.e. straight onto a results page with nothing to show
  // (THE-629 blocker 2). The topic page now forwards that state to the index
  // itself, so this is the second of two guards rather than the only one; it
  // exists to keep the legacy family from manufacturing the case at all,
  // instead of relying on a bounce through a route it should never reach.
  if (!slug || isReservedTopicSlug(slug)) return `/gift-guides${normalizedSearch}`;
  return `/gift-guides/${encodeURIComponent(slug)}${normalizedSearch}`;
}

export const OccasionRedirect: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { search, state } = useLocation();
  // `replace` so the dead URL does not sit in history — a back tap from the
  // topic page should reach wherever the visitor actually came from, not
  // bounce them forward again.
  return <Navigate to={occasionRedirectPath(id ?? '', search)} state={state} replace />;
};

export default OccasionRedirect;
